// src/features/infoscreens/pages/InfoscreenShowPage/index.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Alert, Backdrop, Button, Typography } from '@mui/material';
import { EventAvailable as NoTasksIcon, Login as LoginIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Hooks
import { useInfoscreens } from '../../hooks/useInfoscreens';
import { useScheduledReload } from '../../hooks/useScheduledReload';

// Components
import InfoscreenTopBar from './components/InfoscreenTopBar';
import TasksCardView from './components/TasksCardView';
import TasksListView from './components/TasksListView';
import TasksByUserView from './components/TasksByUserView';

export default function InfoscreenShowPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(['infoscreens', 'tasks', 'common']);
  const { fetchInfoscreenStatus } = useInfoscreens();
  const isReloading = useScheduledReload();

  const [infoscreenData, setInfoscreenData] = useState(null);
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [animationDuration, setAnimationDuration] = useState(30);
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const [isToolbarVisible, setIsToolbarVisible] = useState(false);
  const [animationOffset, setAnimationOffset] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const contentRef = useRef(null);
  const containerRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const SCROLL_SPEED_PX_PER_SEC = 13;

  // List view scrolls manually — no auto-scroll animation
  const canAutoScroll = infoscreenData?.viewmode !== 'list';
  const autoScrollActive = autoScrollEnabled && canAutoScroll;

  // Fullscreen management
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    
    const enterFullscreen = async () => {
      try {
        if (document.documentElement.requestFullscreen && !document.fullscreenElement) {
          await document.documentElement.requestFullscreen();
        }
      } catch (err) {
        console.error('Failed to enter fullscreen:', err);
      }
    };

    const storedData = localStorage.getItem('orbit_perm_infoscreen');
    if (storedData) {
      const data = JSON.parse(storedData);
      if (data.auto_fullscreen !== false) {
        enterFullscreen();
      }
    }

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleToggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Failed to toggle fullscreen:', err);
    }
  };

  // Check if content overflows and calculate animation duration
  useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current && containerRef.current) {
        const contentHeight = contentRef.current.scrollHeight + 25;
        const containerHeight = containerRef.current.clientHeight;
        const overflow = contentHeight > containerHeight;
        setIsOverflowing(overflow);

        if (overflow) {
          const scrollDistance = contentHeight - containerHeight;
          const scrollTime = (scrollDistance * 2) / SCROLL_SPEED_PX_PER_SEC;
          const totalDuration = scrollTime * 1.1;
          setAnimationDuration(totalDuration);
        }
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [statusData]);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Load infoscreen data from localStorage
  useEffect(() => {
    try {
      const storedData = localStorage.getItem('orbit_perm_infoscreen');
      if (!storedData) {
        navigate('/login');
        return;
      }

      const data = JSON.parse(storedData);
      setInfoscreenData(data);
    } catch (err) {
      console.error('Failed to load infoscreen data:', err);
      navigate('/login');
    }
  }, [navigate]);

  // Fetch status data
  const loadStatus = useCallback(async () => {
    if (!infoscreenData) return;

    try {
      setLoading(true);
      const result = await fetchInfoscreenStatus(
        infoscreenData.infoscreen_id,
        infoscreenData.login_code
      );

      if (result.success) {
        setStatusData(result.data);
        setError(null);
      } else {
        setError(result.error || t('infoscreens:errors.failed_to_load_status'));
      }
    } catch (err) {
      setError(t('infoscreens:errors.failed_to_load_status'));
    } finally {
      setLoading(false);
    }
  }, [infoscreenData, fetchInfoscreenStatus, t]);

  // Load status on mount and refresh every 30 seconds
  useEffect(() => {
    if (infoscreenData) {
      loadStatus();
      const interval = setInterval(loadStatus, 30000);
      return () => clearInterval(interval);
    }
  }, [infoscreenData, loadStatus]);

  // Toggle auto-scroll
  const handleToggleAutoScroll = () => {
    if (autoScrollEnabled && scrollContainerRef.current && contentRef.current) {
      const currentScroll = scrollContainerRef.current.scrollTop;
      const contentHeight = contentRef.current.scrollHeight;
      const containerHeight = scrollContainerRef.current.clientHeight;
      const maxScroll = contentHeight - containerHeight;
      
      if (maxScroll > 0) {
        const scrollProgress = currentScroll / maxScroll;
        let timeProgress;
        if (scrollProgress <= 0.5) {
          timeProgress = scrollProgress * 0.9;
        } else {
          timeProgress = 0.5 + (1 - scrollProgress) * 0.45;
        }
        setAnimationOffset(timeProgress * animationDuration);
      }
    } else {
      if (scrollContainerRef.current && contentRef.current) {
        const contentHeight = contentRef.current.scrollHeight;
        const containerHeight = scrollContainerRef.current.clientHeight;
        const maxScroll = contentHeight - containerHeight;
        
        if (maxScroll > 0) {
          const timeProgress = animationOffset / animationDuration;
          let scrollProgress;
          if (timeProgress <= 0.45) {
            scrollProgress = timeProgress / 0.9;
          } else if (timeProgress <= 0.5) {
            scrollProgress = 0.5;
          } else if (timeProgress <= 0.95) {
            scrollProgress = 1 - ((timeProgress - 0.5) / 0.45);
          } else {
            scrollProgress = 0;
          }
          scrollContainerRef.current.scrollTop = scrollProgress * maxScroll;
        }
        setAnimationOffset(0);
      }
    }
    setAutoScrollEnabled(prev => !prev);
  };

  const switchToUserPage = (redirectTo) => {
    if (statusData?.users?.length && infoscreenData?.department_name) {
      localStorage.setItem('orbit_users', JSON.stringify({
        current_user: null,
        available_users: statusData.users,
        original_department: infoscreenData.department_name,
      }));
    }
    navigate('/auth/change-user', { state: { redirectTo } });
  };

  // Handle logout — clears infoscreen session so ChangeUserPage redirects to /home
  const handleLogout = () => {
    localStorage.removeItem('orbit_perm_infoscreen');
    switchToUserPage('/home');
  };

  if (!infoscreenData) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box 
      ref={containerRef}
      sx={{ 
        minHeight: '100vh', 
        height: '100vh',
        bgcolor: 'background.default',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top Bar */}
      <InfoscreenTopBar
        infoscreenData={infoscreenData}
        statusData={statusData}
        currentTime={currentTime}
        isToolbarVisible={isToolbarVisible}
        autoScrollEnabled={autoScrollEnabled}
        canAutoScroll={canAutoScroll}
        isFullscreen={isFullscreen}
        onMouseEnter={() => setIsToolbarVisible(true)}
        onMouseLeave={() => setIsToolbarVisible(false)}
        onToggleAutoScroll={handleToggleAutoScroll}
        onToggleFullscreen={handleToggleFullscreen}
        onLogout={handleLogout}
      />

      {/* Scrollable Content */}
      <Box 
        ref={scrollContainerRef}
        sx={{ 
          flex: 1,
          overflow: autoScrollActive ? 'hidden' : 'scroll',
          position: 'relative',
        }}
      >
        <Box
          ref={contentRef}
          sx={{
            p: 3,
            ...(isOverflowing && autoScrollActive && {
              animation: `scrollContent ${animationDuration}s linear infinite`,
              animationDelay: `-${animationOffset}s`,
            }),
            '@keyframes scrollContent': {
              '0%': { transform: 'translateY(0)' },
              '45%': { transform: 'translateY(calc(-100% + 100vh - 67px))' },
              '50%': { transform: 'translateY(calc(-100% + 100vh - 67px))' },
              '95%': { transform: 'translateY(0)' },
              '100%': { transform: 'translateY(0)' },
            },
          }}
        >
          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Loading State */}
          {loading && !statusData && (
            <Box display="flex" alignItems="center" justifyContent="center" py={8}>
              <CircularProgress />
            </Box>
          )}

          {/* Tasks Display */}
          {statusData && (
            <>
              {statusData.tasks.length === 0 ? (
                // Normally staff log in by tapping a task; with none to tap,
                // offer the change-user screen directly
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  textAlign="center"
                  gap={2}
                  py={12}
                >
                  <NoTasksIcon sx={{ fontSize: 72, color: 'text.disabled' }} />
                  <Typography variant="h5" fontWeight="bold">
                    {t('infoscreens:status.no_tasks_title')}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {t('infoscreens:status.no_tasks')}
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<LoginIcon />}
                    onClick={() => switchToUserPage('/home')}
                    sx={{ mt: 2 }}
                  >
                    {t('infoscreens:status.log_in')}
                  </Button>
                </Box>
              ) : (
                <>
                  {/* Card View */}
                  {(!infoscreenData.viewmode || infoscreenData.viewmode === 'card') && (
                    <TasksCardView tasks={statusData.tasks} />
                  )}

                  {/* List View */}
                  {infoscreenData.viewmode === 'list' && (
                    <TasksListView tasks={statusData.tasks} onSwitchUser={switchToUserPage} />
                  )}

                  {/* User View */}
                  {infoscreenData.viewmode === 'user' && (
                    <TasksByUserView tasks={statusData.tasks} users={statusData.users} onSwitchUser={switchToUserPage} />
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* Scheduled reload overlay */}
      <Backdrop
        open={isReloading}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1, bgcolor: 'rgba(128, 128, 128, 0.85)' }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
}